import { createContext, useEffect, useReducer, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// third-party
import jwtDecode from 'jwt-decode';

// project import
import axios from 'api/axios';
import authReducer, { LOGIN, LOGOUT, initialState } from 'contexts/reducer/auth';
import { Loading } from 'components';

// types
import { KeyedObject } from 'types';
import { AuthContextType, AuthDataProps } from 'types/auth';
import { CLOUD_BASE_PATH } from 'utils/constants';

// サイト識別子
const RENTAL_CODE = process.env.REACT_APP_SERVICE_CODE as string;
// const RENTAL_WEBSITE_ID = Number(process.env.REACT_APP_WEBSITE_ID);
const CLOUD_CODE = process.env.REACT_APP_CLOUD_SERVICE_CODE as string;
// const CLOUD_WEBSITE_ID = Number(process.env.REACT_APP_CLOUD_WEBSITE_ID);

const verifyToken: (st: string) => boolean = (authToken) => {
  if (!authToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(authToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (authToken?: string | null) => {
  if (authToken) {
    localStorage.setItem('authToken', authToken);
    axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  } else {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //

const AuthContext = createContext<AuthContextType | null>(null);

const createPayload = (data: any, service: string) => {
  if (service === CLOUD_CODE) {
    const { orders, cloud_devices, remise_tokens, ...customer } = data;
    const remise_token = remise_tokens[0];
    return { orders, cloud_devices, remise_token, customer } as AuthDataProps;
  } else {
    const { orders, rental_devices, credit_cards, bank_accounts, certificates, ...customer } = data;
    const bank_account = bank_accounts[0];
    return { orders, rental_devices, credit_cards, certificates, bank_account, customer } as AuthDataProps;
  }
};

const getServiceCode = (websiteId: number) => {
  if (websiteId === Number(process.env.REACT_APP_CLOUD_WEBSITE_ID)) {
    return process.env.REACT_APP_CLOUD_SERVICE_CODE as string;
  }
  return process.env.REACT_APP_SERVICE_CODE as string;
};

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
  const location = useLocation();
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [logoutDialog, setLogoutDialog] = useState<boolean>(false);

  const serviceCode = useMemo(() => {
    if (location.pathname.startsWith(CLOUD_BASE_PATH)) return CLOUD_CODE;
    return RENTAL_CODE;
  }, [location.pathname]);

  useEffect(() => {
    let canceled = false;

    const init = async () => {
      try {
        const authToken = window.localStorage.getItem('authToken');
        if (authToken && verifyToken(authToken)) {
          setSession(authToken);
          const { data: response } = await axios.get('/endpoint/customers');
          const { customer } = response.data;
          const isValidEmail = customer.email.indexOf('@') > -1;
          if (canceled) return;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isValidEmail,
              ...createPayload(customer, serviceCode)
            }
          });
        } else if (!canceled) {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        if (!canceled) {
          dispatch({
            type: LOGOUT
          });
        }
      }
    };

    init();

    return () => {
      canceled = true;
    };
  }, [serviceCode]);

  const login = async (email: string, password: string, website_id = Number(process.env.REACT_APP_WEBSITE_ID)) => {
    try {
      const { data: response } = await axios.post('/endpoint/customers/login', { email, password, website_id });
      if (!response.success) {
        throw new Error(response.error.message || 'Unknown error occurred');
      }
      const { token, customer } = response.data;
      setSession(token);
      const isValidEmail = customer.email.indexOf('@') > -1;
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isValidEmail,
          ...createPayload(customer, getServiceCode(website_id))
        }
      });
    } catch (error) {
      throw new Error((error as Error).message || 'Unknown error occurred');
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('orderFormValues');
    localStorage.removeItem('extendFormValues');
    localStorage.removeItem('extendDevices');
    dispatch({ type: LOGOUT });
  };

  const refresh = async (serviceCode: string) => {
    try {
      const { data: response } = await axios.get('/endpoint/customers');
      if (!response.success) {
        throw new Error(response.error || 'Unknown error occurred');
      }
      const { customer } = response.data;
      const isValidEmail = customer.email.indexOf('@') > -1;
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isValidEmail,
          ...createPayload(customer, serviceCode)
        }
      });
    } catch (err) {
      dispatch({
        type: LOGOUT
      });
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh, logoutDialog, setLogoutDialog }}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
